/**
 * Mailing API client.
 *
 * Thin promise wrapper over /compuzz/rest/v2/mailing. Exported directly rather
 * than through reqres handlers: the feature has ~30 endpoints and one handler
 * each would be noise — the views just require this module and call it.
 *
 * Every method resolves with the parsed body and rejects with the jqXHR, so
 * callers can read `xhr.responseJSON.error` for the backend's message.
 */
define('entities/mailing',[
  'module',
  'jquery',
  'underscore',
  'settings'
], function (
  module,
  $,
  _,
  Settings
) {
  'use strict';

  /**
   * The settings endpoint speaks the snake_case keys the standalone app used,
   * so a settings dump from it replays unchanged. Views work in camelCase and
   * the translation happens here, in one place.
   */
  var SETTING_KEYS = {
    smtpHost: 'smtp_host',
    smtpPort: 'smtp_port',
    smtpSecure: 'smtp_secure',
    smtpUser: 'smtp_user',
    smtpPassword: 'smtp_password',
    smtpFromName: 'smtp_from_name',
    queueMaxPerHour: 'queue_max_per_hour',
    queueDelaySeconds: 'queue_delay_seconds'
  };

  function fromWireSettings(wire) {
    var settings = {};
    _.each(SETTING_KEYS, function (wireKey, localKey) {
      settings[localKey] = (wire || {})[wireKey];
    });
    return settings;
  }

  function toWireSettings(settings) {
    var wire = {};
    _.each(SETTING_KEYS, function (wireKey, localKey) {
      if (settings[localKey] !== undefined) {
        wire[wireKey] = String(settings[localKey]);
      }
    });
    return wire;
  }

  function url(path, params) {
    return Settings.url('compuzz2', 'v2/mailing/' + path, params);
  }

  function request(method, path, params, body) {
    var defer = $.Deferred();
    var options = {
      type: method,
      url: url(path, params),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    };
    if (body !== undefined && body !== null) {
      options.contentType = 'application/json';
      options.data = JSON.stringify(body);
    }
    $.ajax(options);
    return defer.promise();
  }

  function get(path, params) { return request('GET', path, params); }
  function post(path, body, params) { return request('POST', path, params, body === undefined ? {} : body); }
  function put(path, body) { return request('PUT', path, null, body); }
  function del(path) { return request('DELETE', path); }

  var API = {

    //----------Subscribers--------------------------------------------------//

    subscribers: function (params) { return get('subscribers', params); },
    subscriber: function (secId) { return get('subscribers/' + secId); },
    createSubscriber: function (data) { return post('subscribers', data); },
    updateSubscriber: function (secId, data) { return put('subscribers/' + secId, data); },
    deleteSubscriber: function (secId) { return del('subscribers/' + secId); },
    subscriberHistory: function (secId) { return get('subscribers/' + secId + '/history'); },
    subscriberClicks: function (secId, sendLogSecId) {
      return get('subscribers/' + secId + '/history/' + sendLogSecId + '/clicks');
    },
    syncSubscribers: function () { return post('subscribers/sync'); },

    //----------Segments-----------------------------------------------------//

    segments: function () { return get('segments'); },
    segment: function (secId) { return get('segments/' + secId); },
    segmentFields: function () { return get('segments/fields'); },
    previewSegment: function (filters) { return post('segments/preview', { filters: filters }); },
    segmentSubscribers: function (secId, limit) {
      return get('segments/' + secId + '/subscribers', { limit: limit || 500 });
    },
    createSegment: function (data) { return post('segments', data); },
    updateSegment: function (secId, data) { return put('segments/' + secId, data); },
    deleteSegment: function (secId) { return del('segments/' + secId); },

    //----------Templates----------------------------------------------------//

    templates: function () { return get('templates'); },
    template: function (secId) { return get('templates/' + secId); },
    createTemplate: function (data) { return post('templates', data); },
    updateTemplate: function (secId, data) { return put('templates/' + secId, data); },
    deleteTemplate: function (secId) { return del('templates/' + secId); },
    sendTestEmail: function (data) { return post('templates/send-test', data); },

    //----------Campaigns----------------------------------------------------//

    campaigns: function () { return get('campaigns'); },
    campaign: function (secId) { return get('campaigns/' + secId); },
    campaignStats: function (secId) { return get('campaigns/' + secId + '/stats'); },
    createCampaign: function (data) { return post('campaigns', data); },
    updateCampaign: function (secId, data) { return put('campaigns/' + secId, data); },
    deleteCampaign: function (secId) { return del('campaigns/' + secId); },
    sendCampaign: function (secId) { return post('campaigns/' + secId + '/send'); },
    scheduleCampaign: function (secId, timestamp) {
      return post('campaigns/' + secId + '/schedule', { scheduledAt: timestamp });
    },
    unscheduleCampaign: function (secId) { return post('campaigns/' + secId + '/unschedule'); },

    //----------Workflows----------------------------------------------------//

    workflows: function () { return get('workflows'); },
    workflow: function (secId) { return get('workflows/' + secId); },
    workflowEnrollments: function (secId) { return get('workflows/' + secId + '/enrollments'); },
    createWorkflow: function (data) { return post('workflows', data); },
    updateWorkflow: function (secId, data) { return put('workflows/' + secId, data); },
    deleteWorkflow: function (secId) { return del('workflows/' + secId); },
    activateWorkflow: function (secId) { return post('workflows/' + secId + '/activate'); },
    pauseWorkflow: function (secId) { return post('workflows/' + secId + '/pause'); },

    //----------Queue--------------------------------------------------------//

    queue: function (params) { return get('queue', params); },
    queueStats: function () { return get('queue/stats'); },
    retryFailed: function () { return post('queue/retry-failed'); },
    cancelQueueItem: function (secId) { return del('queue/' + secId); },

    //----------Settings-----------------------------------------------------//

    settings: function () {
      return get('settings').then(fromWireSettings);
    },
    updateSettings: function (data) {
      return put('settings', toWireSettings(data)).then(fromWireSettings);
    },
    testSmtp: function () { return post('settings/test-smtp'); },

    //----------Helpers------------------------------------------------------//

    /** Backend error message, or a generic fallback. */
    errorMessage: function (xhr) {
      if (xhr && xhr.responseJSON && xhr.responseJSON.error) {
        return xhr.responseJSON.error;
      }
      if (xhr && xhr.status === 403) {
        return 'You are not allowed to do this';
      }
      return 'Something went wrong, please try again';
    }
  };

  module.exports = API;
});

